import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';
import {
  Scale, Eye, Store, Tag, ShieldCheck, Sparkles, ArrowRight, Target, Telescope, BookOpen,
} from 'lucide-react';

// ============== HERO ==============
const Hero = () => (
  <section
    className="relative pt-36 sm:pt-40 lg:pt-48 pb-16 lg:pb-24 overflow-hidden"
    data-testid="about-hero"
  >
    {/* Cinematic restaurant background — warm gold-tinted */}
    <div className="absolute inset-0 z-0">
      <img
        src="/brand/bar_atmosphere.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-[0.3]"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(212,175,55,0.10),transparent_60%)]" />
    </div>

    <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
      <p
        data-testid="about-eyebrow"
        className="text-[11px] uppercase tracking-[0.32em] text-gold-400 font-semibold mb-6"
      >
        Our Story
      </p>
      <h1 className="text-4xl sm:text-5xl lg:text-[64px] xl:text-[72px] font-black tracking-tight text-white leading-[1.05] max-w-4xl mx-auto">
        Food delivery should work for <span className="text-gold-400">everyone</span>{' '}
        — not against them.
      </h1>
    </div>
  </section>
);

// ============== MISSION ==============
const Mission = () => (
  <section className="relative pb-16 lg:pb-20" data-testid="about-mission">
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
      <div className="rounded-3xl border border-zinc-800 bg-gradient-to-br from-zinc-950 to-black p-8 lg:p-12">
        <div className="flex items-start gap-5">
          <div className="w-12 h-12 rounded-2xl bg-gold-500/[0.06] border border-gold-500/30 flex items-center justify-center flex-shrink-0">
            <Target className="w-5 h-5 text-gold-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-semibold mb-3">
              Mission
            </p>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight">
              Our Mission
            </h2>
            <p className="mt-5 text-[16px] sm:text-lg text-white/65 leading-[1.8] max-w-3xl">
              To create a fairer food delivery ecosystem where restaurants can grow
              without losing a large percentage of their revenue to excessive
              commissions — while customers enjoy better prices and a more
              transparent experience.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============== VALUES ==============
const Values = () => {
  const values = [
    {
      icon: Scale,
      title: 'Fairness',
      body: 'Equal opportunity for every restaurant and every customer.',
    },
    {
      icon: Eye,
      title: 'Transparency',
      body: 'No hidden fees. No surprise markups. Clear pricing, always.',
    },
    {
      icon: Store,
      title: 'Support Local Restaurants',
      body: 'We help local businesses grow without losing profit to commissions.',
    },
    {
      icon: Tag,
      title: 'Better Prices For Customers',
      body: 'When restaurants keep more, customers pay less. Everyone wins.',
    },
    {
      icon: ShieldCheck,
      title: 'Direct & Secure Payments',
      body: 'Payments go directly to your own Stripe account. NoFee never holds your money.',
    },
    {
      icon: Sparkles,
      title: 'Simplicity',
      body: 'A clean, modern experience built to feel effortless for everyone.',
    },
  ];

  return (
    <section
      className="relative py-16 lg:py-20 border-t border-white/[0.06]"
      data-testid="about-values"
    >
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
        <div className="mb-10 lg:mb-12">
          <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-semibold mb-3">
            Values
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">
            What we stand for
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {values.map(({ icon: Icon, title, body }) => (
            <article
              key={title}
              data-testid={`about-value-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
              className="rounded-2xl bg-gradient-to-br from-zinc-950 to-black border border-zinc-800 hover:border-gold-500/35 p-7 lg:p-8 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-gold-500/[0.06] border border-gold-500/30 flex items-center justify-center mb-5">
                <Icon className="w-5 h-5 text-gold-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-[17px] font-extrabold text-white tracking-tight">
                {title}
              </h3>
              <p className="mt-2.5 text-sm text-white/60 leading-relaxed">{body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============== VISION ==============
const Vision = () => (
  <section
    className="relative py-16 lg:py-20 border-t border-white/[0.06]"
    data-testid="about-vision"
  >
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
      <div className="flex items-start gap-5">
        <div className="w-12 h-12 rounded-2xl bg-gold-500/[0.06] border border-gold-500/30 flex items-center justify-center flex-shrink-0">
          <Telescope className="w-5 h-5 text-gold-400" strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-semibold mb-3">
            Vision
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white leading-tight">
            Our Vision
          </h2>
          <p className="mt-5 text-[16px] sm:text-lg text-white/65 leading-[1.8] max-w-3xl">
            We envision a future where restaurants have more control, customers
            pay less, and food delivery becomes more sustainable, transparent,
            and beneficial for everyone involved.
          </p>
          <p className="mt-4 text-[16px] sm:text-lg text-white/65 leading-[1.8] max-w-3xl">
            NoFee aims to become a trusted platform that connects local
            restaurants and customers through innovation, simplicity, and real
            value.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// ============== COMPANY STORY ==============
const Story = () => (
  <section
    className="relative py-16 lg:py-20 border-t border-white/[0.06]"
    data-testid="about-story"
  >
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
      <div className="flex items-start gap-5">
        <div className="w-12 h-12 rounded-2xl bg-gold-500/[0.06] border border-gold-500/30 flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-5 h-5 text-gold-400" strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-semibold mb-3">
            Company Story
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white leading-tight">
            How NoFee began
          </h2>
          <p className="mt-5 text-[16px] sm:text-lg text-white/65 leading-[1.8] max-w-3xl">
            NoFee was created to help restaurants keep more profit and help
            customers pay less for food. We believe local restaurants deserve
            better conditions, direct payouts, and real support — without
            massive commissions and hidden fees.
          </p>

          <ul className="mt-7 space-y-3.5">
            {[
              'More profit for restaurants',
              'Better prices for customers',
              'A fairer food delivery experience for everyone',
            ].map((line) => (
              <li
                key={line}
                className="flex items-start gap-3 text-[16px] text-white/85 leading-snug"
              >
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gold-400 flex-shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// ============== FINAL CTA ==============
const FinalLine = () => (
  <section
    className="relative py-20 lg:py-28 border-t border-white/[0.06] overflow-hidden"
    data-testid="about-final"
  >
    <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.08),transparent_65%)]" />
    <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white leading-tight max-w-3xl mx-auto">
        Built to help restaurants <span className="text-gold-400">grow</span> —
        not lose profit.
      </h2>

      <div className="mt-9 space-y-1.5 text-base sm:text-lg text-white/65 leading-relaxed">
        <p>More profit for restaurants.</p>
        <p>Better prices for customers.</p>
        <p className="text-white/85">Better food delivery for everyone.</p>
      </div>

      <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/start?plan=ordering"
          data-testid="about-cta-restaurant"
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold transition-all shadow-[0_15px_40px_-18px_rgba(212,175,55,0.55)]"
        >
          Join as a Restaurant <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to="/customer"
          data-testid="about-cta-customer"
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl border border-gold-500/40 hover:border-gold-500/70 text-gold-300 hover:text-gold-200 font-bold transition-colors"
        >
          Sign Up as a Customer <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);

// ============== PAGE ==============
export default function AboutUs() {
  return (
    <LanguageProvider>
      <SEO
        title="About NoFee — Commission-Free Restaurant Platform"
        description="NoFee is rebuilding restaurant delivery so that 100% of every order goes to the restaurant. Learn our mission, values, and story behind the 0% commission platform."
        path="/about"
      />
      <div className="min-h-screen bg-black text-white antialiased" data-testid="about-page">
        <Navbar />
        <Hero />
        <Mission />
        <Values />
        <Vision />
        <Story />
        <FinalLine />
        <FooterSection />
      </div>
    </LanguageProvider>
  );
}
